import { type APIPaginatedResponse, EndpointService } from "@/types/api/api";
import { buildQueryKey } from "@/utils/query";
import {
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import type { IAPI } from "../APIService";
import type { ISearchOrganisationItem } from "../Operations/Items";

export interface IPackage {
  description: string;
  error_message: string;
  name: string;
  daily_cap: number;
  id: number;
  redemption_limit: number;
  created_at: string;
  updated_at: string;
}

export interface IListPackage extends IPackage {
  menu_item_count: number;
  redeemable_configuration_count: number;
}

export interface IListPackagesResponse
  extends APIPaginatedResponse<IListPackage> {}

export interface IListPackagesParams {
  search?: string;
  configuration_id?: number;
  venue_id?: number;
  page?: number;
  page_size?: number;
}

export interface IListPackagesResponseAll {
  pages: Array<IListPackagesResponse>;
  pageParams: number[];
}

export interface ICreatePackageParams {
  name: string;
  redemption_limit: number | string;
  daily_cap: number;
  description: string;
  error_message: string;
}

export interface IUpdatePackageParams
  extends Omit<ICreatePackageParams, "redemption_limit" | "daily_cap"> {
  id: number | string;
}

export interface IRetrievePackageParams {
  id: number | string;
}

export interface IDeletePackageParams {
  id: number | string;
}

export interface IListMenuItemsParams {
  package_id: number | string;
}

export interface IItemPackageMenuItem {
  id: number;
  menu_item: {
    restaurant: {
      name: string;
      id: number | string;
      created_at: string;
      updated_at: string;
    };
    value: number;
    created_at: string;
    name: string;
    id: number;
    display_name: string;
    updated_at: string;
    category_names: Array<string>;
  };
  created_at: string;
  updated_at: string;
}

export interface IListMenuItemsResponse
  extends APIPaginatedResponse<IItemPackageMenuItem> {}

export interface ICreateUpdatePackageMenuItemParams {
  package_id: number | string;
  creation_menu_item_ids: Array<number>;
  deletion_menu_item_ids: Array<number>;
}

/**
 * Interface representing the parameters for duplicating an item package.
 * @interface IDuplicatePackageParams
 * @property {number | string} package_id — The ID of the source package to duplicate.
 * @property {string} name — The name for the duplicated package.
 * @property {string} description — The description for the duplicated package.
 * @property {number | string} [redemption_limit] — Optional override for the duplicate's redemption limit. Integer >= 1 or "*" for unlimited. Omitted entirely to inherit the source package's value.
 */
export interface IDuplicatePackageParams {
  package_id: number | string;
  name: string;
  description: string;
  redemption_limit?: number | string;
}

/**
 * Interface representing the service contract for item packages.
 * @interface IPackagesService
 * @property {Function} list — Lists packages with optional filtering params.
 * @property {Function} listAll — Lists all packages with infinite scrolling.
 * @property {Function} retrieve — Retrieves a single package by ID.
 * @property {Function} create — Creates a new package.
 * @property {Function} update — Updates an existing package.
 * @property {Function} delete — Deletes a package by ID.
 * @property {Function} duplicate — Duplicates a package with a new name and description.
 * @property {Function} listMenuItems — Lists menu items linked to a package.
 * @property {Function} createUpdateMenuItems — Bulk creates or updates menu items on a package.
 */
export interface IPackagesService {
  list: (
    params?: IListPackagesParams,
  ) => UseQueryResult<IListPackagesResponse, Error>;
  listAll: (
    params?: IListPackagesParams,
  ) => UseInfiniteQueryResult<IListPackagesResponseAll, Error>;
  retrieve: (params: IRetrievePackageParams) => UseQueryResult<IPackage, Error>;
  create: (
    listParams?: IListPackagesParams,
  ) => UseMutationResult<IPackage, Error, ICreatePackageParams>;
  update: () => UseMutationResult<IPackage, Error, IUpdatePackageParams>;
  delete: () => UseMutationResult<boolean, Error, IDeletePackageParams>;
  duplicate: () => UseMutationResult<IPackage, Error, IDuplicatePackageParams>;

  // Item Package Menu Items
  listMenuItems: (
    params?: IListMenuItemsParams,
  ) => UseQueryResult<IListMenuItemsResponse, Error>;
  createUpdateMenuItems: (
    listParams?: Record<string, string | number | boolean>,
  ) => UseMutationResult<
    IListMenuItemsResponse,
    Error,
    ICreateUpdatePackageMenuItemParams
  >;
}

export class PackagesService implements IPackagesService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(
    params?: IListPackagesParams,
  ): UseQueryResult<IListPackagesResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "packages", "list"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IListPackagesResponse>
        >("/promotions/packages/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: updatedParams,
        });
        return data;
      },
    });
  }

  listAll(
    params?: IListPackagesParams,
  ): UseInfiniteQueryResult<IListPackagesResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );

    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "packages", "list-all"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<IListPackagesResponse>
        >("/promotions/packages/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: { ...updatedParams, page: pageParam, page_size: 50 },
        });
        return data;
      },
      getNextPageParam: (lastPage: IListPackagesResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  retrieve(params: IRetrievePackageParams): UseQueryResult<IPackage, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "packages", "retrieve", params.id],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IPackage>>(
          `/promotions/packages/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      enabled: !!params.id,
    });
  }

  create(
    listParams?: IListPackagesParams,
  ): UseMutationResult<IPackage, Error, ICreatePackageParams> {
    const queryClient = useQueryClient();
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "packages", "create"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const updatedParams = Object.fromEntries(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null,
          ),
        );

        const { data } = await this.api.post<AxiosResponse<IPackage>>(
          "/promotions/packages/",
          {
            params: updatedParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onMutate: async (newPackage) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "packages", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
        const previousPackages =
          queryClient.getQueryData<IListPackagesResponse>(
            buildQueryKey({
              queryKey: ["redeemables", "packages", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
          );
        if (previousPackages) {
          queryClient.setQueryData<IListPackagesResponse>(
            buildQueryKey({
              queryKey: ["redeemables", "packages", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            (old) => {
              return {
                ...old,
                results: [
                  ...old.results,
                  {
                    id: -1,
                    name: newPackage.name,
                    redemption_limit: newPackage.redemption_limit as number,
                    daily_cap: newPackage.daily_cap,
                    description: newPackage.description,
                    error_message: newPackage.error_message,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    menu_item_count: 0,
                    redeemable_configuration_count: 0,
                  },
                ],
                count: old.count + 1,
                next: old.next,
                previous: old.previous,
              };
            },
          );
        }
        return { previousPackages };
      },
      onError: async (err, newPackage, context) => {
        if (context?.previousPackages) {
          await queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "packages", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            context.previousPackages,
          );
        }
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "packages", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }

  update(): UseMutationResult<IPackage, Error, IUpdatePackageParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "packages", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<IPackage>>(
          `/promotions/packages/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: restParams,
          },
        );
        return data;
      },
      onMutate: async (newPackage) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "packages", "retrieve", newPackage.id],
            venueID: this.api.selectedVenue,
            params: { id: newPackage.id },
          }),
        });
        const previousPackage = await queryClient.getQueryData<IPackage>(
          buildQueryKey({
            queryKey: ["redeemables", "packages", "retrieve", newPackage.id],
            venueID: this.api.selectedVenue,
            params: { id: newPackage.id },
          }),
        );
        if (previousPackage) {
          await queryClient.setQueryData<IPackage>(
            buildQueryKey({
              queryKey: ["redeemables", "packages", "retrieve", newPackage.id],
              venueID: this.api.selectedVenue,
              params: { id: newPackage.id },
            }),
            (old) => {
              return {
                ...old,
                ...newPackage,
                id: newPackage.id as number,
              };
            },
          );
        }
        return { previousPackage };
      },
      onError: async (err, newPackage, context) => {
        if (context?.previousPackage) {
          await queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "packages", "retrieve", newPackage.id],
              venueID: this.api.selectedVenue,
              params: { id: newPackage.id },
            }),
            context.previousPackage,
          );
        }
      },
      onSettled: async (data, error, variables) => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "packages", "retrieve", variables.id],
            venueID: this.api.selectedVenue,
            params: { id: variables.id },
          }),
        });
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeletePackageParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "packages", "delete"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.delete<AxiosResponse<string>>(
          `/promotions/packages/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data === "";
      },
    });
  }

  duplicate(): UseMutationResult<IPackage, Error, IDuplicatePackageParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "packages", "duplicate"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { package_id, ...restParams } = params;
        const updatedParams = Object.fromEntries(
          Object.entries(restParams).filter(
            ([, value]) => value !== undefined && value !== null,
          ),
        );
        const { data } = await this.api.post<AxiosResponse<IPackage>>(
          `/promotions/packages/${package_id}/duplicate/`,
          {
            params: updatedParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "packages", "list"],
            venueID: this.api.selectedVenue,
            params: {},
          }),
        });
      },
    });
  }

  // Item Package Menu Items
  listMenuItems(
    params?: IListMenuItemsParams,
  ): UseQueryResult<IListMenuItemsResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "packages", "listMenuItems"],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { package_id } = updatedParams;
        const { data } = await this.api.get<
          AxiosResponse<IListMenuItemsResponse>
        >(`/promotions/packages/${package_id}/menu_items/`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: updatedParams,
        });
        return data;
      },
    });
  }

  createUpdateMenuItems(
    listParams?: Record<string, string | number | boolean>,
  ): UseMutationResult<
    IListMenuItemsResponse,
    Error,
    ICreateUpdatePackageMenuItemParams
  > {
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "packages", "createUpdateMenuItems"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { package_id, ...restParams } = params;
        const { data } = await this.api.post<
          AxiosResponse<IListMenuItemsResponse>
        >(`/promotions/packages/${package_id}/menu_items/bulk/`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: restParams,
        });
        return data;
      },

      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "packages", "listMenuItems"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });

        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: [
              "organisation",
              "items",
              "search-all-organisation-items",
            ],
            venueID: this.api.selectedVenue,
            params: {},
          }),
        });
      },
    });
  }
}
